'use no memo'

import { unbanUser, updateAppUser, useUser, useUserAdminForm } from '@gozoki/api'
import { Box, Button, IconButton, Typography } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import z from 'zod/v3'
import { AuthorizedButton } from '~/components/communication/AuthorizedButton'
import { useToast } from '~/components/communication/Toast'
import LoadingPaper from '~/components/containers/LoadingPaper'
import TextFieldRhf from '~/components/inputs/TextFieldRhf'
import Page from '~/components/Page'
import { useMigratedRight, withRoutePermissions } from '~/utils/rbac'
import { getErrorMessageWithSentry } from '~/utils/sentry'
import { ThemeColors } from '~/utils/theme/colors.theme'
import { fonts } from '~/utils/theme/fonts.theme'
import { ButtonOverrides } from '~/utils/theme/overrides.theme'

export const Route = createFileRoute('/_authenticated/users/edit/$userId')({
  staticData: {
    permission: ['read', 'users.app-users.user'],
  },
  params: {
    parse: z.object({ userId: z.coerce.number() }).parse,
  },
  component: EditUserPage,
})
withRoutePermissions(Route)

function EditUserPage() {
  const navigate = Route.useNavigate()
  const { userId } = Route.useParams()

  // Use isLoading to show a loading indicator before the data arrives
  const { data: userData, isLoading } = useUser(userId)

  const { control, trigger, getValues, setValue } = useUserAdminForm()

  const [Toast, showToast] = useToast()

  const [modifyPassword, setModifyPassword] = useState(false)

  const canEdit = useMigratedRight(['edit', 'users.app-users.user'], ['SUPER_ADMIN'])

  // Fill the form with initial data once the data is fetched
  useEffect(() => {
    if (userData) {
      setValue('firstName', userData.firstName)
      setValue('lastName', userData.lastName)
      setValue('email', userData.email)
    }
  }, [setValue, userData])

  const navigateBack = useCallback(() => {
    navigate({ to: '/users/app' })
  }, [navigate])

  // ******************************************************************** //
  //                              MUTATIONS                               //
  // ******************************************************************** //

  const { mutate: updateApp, isPending: isUpdatingApp } = useMutation({
    mutationFn: updateAppUser,
    onError: (error) => {
      showToast({
        severity: 'error',
        message: getErrorMessageWithSentry(error),
      })
    },
    onSuccess: navigateBack,
  })

  const { mutate: handleUnban, isPending: isUnbanning } = useMutation({
    mutationFn: (id: number) => unbanUser(id),
    onSuccess: navigateBack,
    onError: (error) => {
      showToast({
        severity: 'error',
        message: getErrorMessageWithSentry(error),
      })
    },
  })

  // ******************************************************************** //
  //                               HANDLERS                               //
  // ******************************************************************** //

  const handleUpdateUser = async () => {
    const valid = await trigger() // Trigger form validation

    if (valid) {
      const values = getValues()
      updateApp({
        userId,
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
      })
    } else {
      showToast({
        message: 'Veuillez remplir tous les champs obligatoires',
        severity: 'error',
      })
    }
  }

  return (
    <Page sx={{ margin: 'auto', maxWidth: '50vw' }}>
      <Toast />
      <div style={{ alignItems: 'center', display: 'flex' }}>
        <IconButton onClick={navigateBack}>
          <ArrowLeft
            style={{
              color: ThemeColors.BLACK,
              height: '24px',
              width: '24px',
            }}
          />
        </IconButton>
        <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
          {canEdit ? 'Modifier un utilisateur' : 'Fiche utilisateur'}
        </Typography>

        <Button
          variant="outlined"
          sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
          onClick={navigateBack}
        >
          {canEdit ? 'Annuler' : 'Retour'}
        </Button>
        <AuthorizedButton
          permission={['edit', 'users.app-users.user']}
          legacyRights={['SUPER_ADMIN']}
          variant="contained"
          disabled={isUpdatingApp}
          onClick={handleUpdateUser}
        >
          Modifier
        </AuthorizedButton>
      </div>
      <LoadingPaper
        sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
        loading={isLoading}
      >
        <Typography mb="8px" style={fonts.inputHeader}>
          Prénom
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Prénom"
          color="info"
          control={control}
          trigger={trigger}
          name="firstName"
          fullWidth
        />
        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Nom
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Nom"
          color="info"
          control={control}
          trigger={trigger}
          name="lastName"
          fullWidth
        />

        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Email
        </Typography>
        <TextFieldRhf
          variant="outlined"
          placeholder="Email"
          color="info"
          control={control}
          trigger={trigger}
          name="email"
          fullWidth
        />

        <Typography mb="8px" mt="24px" style={fonts.inputHeader}>
          Mot de passe
        </Typography>
        {modifyPassword ? (
          <TextFieldRhf
            variant="outlined"
            placeholder="Mot de passe"
            color="info"
            control={control}
            trigger={trigger}
            name="password"
            fullWidth
            validateRealTime
          />
        ) : (
          <Button onClick={() => setModifyPassword(true)}>Modifier le mot de passe</Button>
        )}
      </LoadingPaper>
      {userData && 'isBanned' in userData && userData.isBanned && (
        <Box sx={{ marginTop: '16px' }}>
          <AuthorizedButton
            permission={['edit', 'users.app-users.ban']}
            legacyRights={['SUPER_ADMIN']}
            variant="contained"
            color="warning"
            sx={{ marginLeft: '16px' }}
            disabled={isUnbanning}
            onClick={() => handleUnban(userData.id)}
          >
            Débloquer l'utilisateur
          </AuthorizedButton>
        </Box>
      )}
    </Page>
  )
}

export default EditUserPage
